import type { ImageBlockProps, ImageBlockImage } from './types'

function Caption({ caption, className = '' }: { caption?: string; className?: string }) {
	if (!caption) return null
	return <figcaption className={`text-base leading-[1.6] text-black-600 ${className}`}>{caption}</figcaption>
}

function ImageBox({ image, className }: { image: ImageBlockImage; className: string }) {
	return (
		<div className={`relative overflow-hidden rounded-xs ${className}`}>
			<img src={image.src} alt={image.alt} className="absolute inset-0 w-full h-full object-cover" />
		</div>
	)
}

function SingleImage({ image }: { image?: ImageBlockImage }) {
	if (!image) return null
	return (
		<figure>
			<ImageBox image={image} className="aspect-video" />
			<Caption caption={image.caption} className="pt-4" />
		</figure>
	)
}

export default function ImageBlock({ images = [], isPart = false }: ImageBlockProps) {
	const [image1, image2] = images

	if (images.length < 2 && !image1) {
		return null
	}

	const content =
		images.length < 2 ? (
			<SingleImage image={image1} />
		) : (
			<div>
				{/* mobile — offset/overlapping composition. Captions sit in one row below
				    both images, side by side, instead of stacking. */}
				<div className="md:hidden">
					{image1 && <ImageBox image={image1} className="size-38 aspect-square mb-[-3.75rem]" />}
					{image2 && <ImageBox image={image2} className="ml-12 aspect-328/180" />}
					{(image1?.caption || image2?.caption) && (
						<div className="flex flex-wrap gap-x-6">
							<Caption caption={image1?.caption} className="pt-4" />
							<Caption caption={image2?.caption} className="pt-4" />
						</div>
					)}
				</div>

				{/* desktop/tablet — a 2-column grid where row 1 (the images) stretches
				    both columns to the same height and row 2 (the captions) sizes
				    independently, so a caption can never overflow into the next block. */}
				<div className="hidden md:grid grid-cols-[1fr_1.832fr] items-stretch gap-x-8">
					{image1 && (
						<figure className="contents">
							<ImageBox image={image1} className="col-start-1 row-start-1 aspect-square" />
							<Caption caption={image1.caption} className="col-start-1 row-start-2 pt-4" />
						</figure>
					)}
					{image2 && (
						<figure className="contents">
							<ImageBox image={image2} className="col-start-2 row-start-1 h-full" />
							<Caption caption={image2.caption} className="col-start-2 row-start-2 pt-4" />
						</figure>
					)}
				</div>
			</div>
		)

	if (isPart) {
		return <div className="container my-16">{content}</div>
	}

	return <div className="my-12">{content}</div>
}
